export class WebSocketUtil {
  private socketTask = null
  private is_open_socket: boolean = false // 避免重复连接
  private url: string
  private timeout: number // 心跳检测间隔
  private heartbeatInterval: any = null // 服务器心跳检测
  private reconnectTimeOut: any = null // 断线重连定时器
  private callMessage: () => void

  // STOMP 协议 CONNECT 消息
  private stomp: string =
    'CONNECT\nlogin:guest\npasscode:guest\naccept-version:1.1,1.0\nheart-beat:5000,5000\n\n\u0000'

  // 维持心跳的 SUBSCRIBE 订阅消息
  private tcp: string = 'SUBSCRIBE\nid:sub-0\ndestination:/price/all\n\n\u0000'

  constructor(url: string, time: number, callMessage: () => void) {
    this.url = url
    this.timeout = time
    this.callMessage = callMessage

    try {
      this.connectSocketInit()
    } catch (e) {
      console.error('WebSocket 连接异常:', e)
      this.is_open_socket = false
      this.reconnect()
    }
  }

  /**
   * 初始化 WebSocket 连接
   */
  private connectSocketInit() {
    console.log('正在连接 WebSocket:', this.url)

    // 先清理之前的连接
    this.close()

    // 连接 WebSocket
    this.socketTask = uni.connectSocket({
      url: this.url,
      header: {
        'content-type': 'application/json',
      },
      protocols: [],
      success: () => console.log('WebSocket 连接成功'),
      fail: (err) => console.error('WebSocket 连接失败:', err),
    })

    this.socketTask.onOpen(() => {
      console.log('WebSocket 连接已打开')
      clearTimeout(this.reconnectTimeOut)
      this.is_open_socket = true

      // 发送 STOMP 协议
      this.send(this.stomp)
      this.send(this.tcp)

      this.start() // 维持心跳
      // 监听消息
      this.socketTask?.onMessage((message) => {
        const content = message.data as string
        const strArray: string[] = content.trim().split('\n') // 去除首尾空白，防止换行符干扰
        let base64Str: string = strArray[strArray.length - 1] // 获取最后一行并去除空格
        base64Str = base64Str.replace(/[\r\n]/g, '').replace(/\s+/g, '')
        base64Str = base64Str
          .replace(/-/g, '+') // 将 "-" 替换为 "+"
          .replace(/_/g, '/') // 将 "_" 替换为 "/"
          .replace(/[^A-Za-z0-9+/=]/g, '') // 彻底删除非法字符
        // 确保 Base64 字符串的长度为4的倍数
        while (base64Str.length % 4 !== 0) {
          base64Str += '='
        }
        this.callMessage(base64Str)
      })
    })

    this.socketTask.onClose(() => {
      console.log('WebSocket 连接已关闭')
      this.is_open_socket = false
      this.reconnect()
    })

    this.socketTask.onError((err) => {
      console.error('WebSocket 错误:', err)
      this.is_open_socket = false
      this.reconnect()
    })
  }

  /**
   * 发送数据
   * @param value 发送的消息
   */
  public send(value: string) {
    if (!this.is_open_socket || !this.socketTask) {
      console.warn('WebSocket 连接未建立，无法发送消息')
      return
    }

    this.socketTask.send({
      data: value,
      success: () => {},
      fail: () => {},
    })
  }

  /**
   * 维持心跳检测（**修复多次执行 setInterval 导致的性能问题**）
   */
  private start() {
    console.log('开始心跳检测...')

    // **清除旧的定时器，避免重复创建**
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }

    // **只允许一个心跳定时器运行**
    this.heartbeatInterval = setInterval(() => {
      this.send(this.tcp)
    }, this.timeout)
  }

  /**
   * 断线重连（**确保旧连接完全清理后再重新连接**）
   */
  private reconnect() {
    clearInterval(this.heartbeatInterval)

    if (!this.is_open_socket) {
      console.log('尝试重新连接 WebSocket...')
      this.reconnectTimeOut = setTimeout(() => {
        this.connectSocketInit()
      }, 3000)
    }
  }

  /**
   * 关闭 WebSocket 连接（**彻底清理所有定时器，防止内存泄漏**）
   */
  public close() {
    console.log('正在关闭 WebSocket...')
    this.is_open_socket = false

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.reconnectTimeOut) {
      clearTimeout(this.reconnectTimeOut)
      this.reconnectTimeOut = null
    }

    if (this.socketTask) {
      this.socketTask.close()
      this.socketTask = null
    }
  }

  /**
   * 监听消息
   * @param callback 回调函数
   */
  public getMessage(callback: () => void) {
    this.socketTask?.onMessage((res) => {
      callback(res.data)
    })
  }
}

/**
 * 创建 WebSocket 连接
 * @param url WebSocket 服务器地址
 * @param time 心跳间隔（毫秒）
 * @param callMessage 消息回调函数
 */
const socket = (url: string, time: number, callMessage: () => void) => {
  return new WebSocketUtil(url, time, callMessage)
}

export default socket
